import React, { useState, useEffect, useCallback } from "react";
import { Container, Button, Row, Col, Form } from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import moment from "moment";
import "../../static/css/App.css";
import "../../static/css/customerTable.css";
import "bootstrap/dist/css/bootstrap.min.css";
import DynamicBreadcrumb from "../header/breadcrumb";
import reportApi from "../../services/reportapi";
import Loader from "../loader/loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import branchApi from "../../services/branchapi";
library.add(fas);

const SaleOrders = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortModel, setSortModel] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 15,
    });
    const [loaderVisible, setLoaderVisible] = useState(false);
    const [branches, setBranches] = useState([]);
    const [filters, setFilters] = useState({
        fromDate: "",
        toDate: "",
        fieldType: "",
        fieldFDate: "",
        fieldTDate: "",
        branchId: "",
    });

    const updateFilters = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const fetchRecord = useCallback(async () => {
        setLoaderVisible(true);
        setLoading(true);
        try {
            const { page, pageSize } = paginationModel;
            const payload = {
                limit: pageSize,
                page,
                sortModel,
                filterModel,
                ...filters,
                search: "",
            };

            const data = await reportApi.getSaleOrders(payload);

            const formattedRows = data.data.result.map((emp, index) => ({
                ...emp,
                sno: index + 1 + page * pageSize,
                branchDetails: emp.branchDetails?.branchName || "",
                customerDetails: `${emp.customerDetails?.name}  ${emp.customerDetails.mobile}` || "",
                created_at: moment(emp.postedDate).format("YYYY-MM-DD HH:mm"),
                status: getStatusText(emp.status),
                deliveryTypeText: getDeliveryTypeText(emp.deliveryType),
            }));

            setRows(formattedRows);
            setTotalRecords(data.data.totalRecords || 0);
        } catch (error) {
            console.error("Failed to fetch report:", error);
        } finally {
            setLoading(false);
            setLoaderVisible(false);
        }
    }, [paginationModel, sortModel, filterModel, filters]);

    useEffect(() => {
        fetchRecord();
    }, [fetchRecord]);

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const branchData = await branchApi.getAllBranches({
                    limit: 100,
                    page: 0,
                    sortModel: [{ field: "branchName", sort: "asc" }],
                })
                setBranches(branchData.data.result || []);
            } catch (error) {
                console.error("Error fetching dropdown data:", error);
            }
        }
        fetchBranches();
    }, []);

    const getStatusText = (code) => {
        const statusMap = {
            1: "Pending",
            2: "Confirmed",
            3: "Admin Cancel",
            4: "Packed",
            5: "Shipped",
            6: "Delivered",
            7: "Customer Cancel",
            8: "Customer Refund",
            9: "Deleted",
            10: "Refund Approved",
            11: "Feedback"
        };
        return statusMap[code] || "Unknown";
    };

    const getDeliveryTypeText = (type) => {
        const typeMap = {
            1: "Regular Delivery",
            2: "Express Delivery",
            3: "Take Away",
            4: "Standard Delivery"
        };
        return typeMap[type] || "Unknown";
    };

    const exportData = async () => {
        try {
            setLoaderVisible(true);

            const payload = {
                limit: totalRecords,
                page: 0,
                sortModel,
                filterModel,
                ...filters,
                search: "",
            };

            const data = await reportApi.getSaleOrders(payload);


            const allRows = data.data.result.map((emp, index) => ({
                sno: index + 1,
                showId: emp.showId,
                customer: emp.customerDetails?.name || "",
                branch: emp.branchDetails?.branchName || "",
                itemCount: emp.itemCount,
                totalAmount: emp.totalAmount,
                deliveryType: getDeliveryTypeText(emp.deliveryType),
                status: getStatusText(emp.status),
                deliveryDate: emp.deliveryDate,
                postedDate	: moment(emp.postedDate).format("YYYY-MM-DD"),
            }));

            const csvRows = [
                ["SNO", "Created Date", "Order ID", "Customer", "Branch", "Total Items", "Amount", "Delivery Type", "Status", "Delivery Date"],
            ];

            allRows.forEach((row) => {
                csvRows.push([
                    row.sno,
                   row.postedDate,
                    row.showId,
                    row.customer,
                    row.branch,
                    row.itemCount,
                    row.totalAmount,
                    row.deliveryType,
                    row.status,
                    row.deliveryDate,
                ]);
            });

            const csvContent = csvRows.map((e) => e.join(",")).join("\n");
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", "sale_orders_all.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setLoaderVisible(false);
        }
    };

    const columns = [
        { field: "sno", headerName: "S NO", width: 80 },
        { field: "created_at", headerName: "Updated ON", flex: 1 },
        { field: "showId", headerName: "Order ID", flex: 1 },
        { field: "branchDetails", headerName: "Branch Name", flex: 1 },
        { field: "customerDetails", headerName: "Customer", flex: 1 },
        { field: "itemCount", headerName: "Total Items", flex: 1 },
        { field: "totalAmount", headerName: "Total Amount", flex: 1 },
        { field: "deliveryTypeText", headerName: "Delivery Type", flex: 1 },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            renderCell: (params) => {
                const colorMap = {
                    "Pending": "gray",
                    "Confirmed": "blue",
                    "Admin Cancel": "red",
                    "Packed": "orange",
                    "Shipped": "purple",
                    "Delivered": "green",
                    "Customer Cancel": "red",
                    "Customer Refund": "darkred",
                    "Deleted": "black",
                    "Refund Approved": "teal",
                    "Feedback": "gold"
                };
                return (
                    <span style={{ color: colorMap[params.value] || "black", fontWeight: 500 }}>
                        {params.value}
                    </span>
                );
            }
        }
    ];

    const pathArray = [{ name: "Sale Order Report", url: null }];

    return (
        <Container className="mt-4 mb-4">
            <DynamicBreadcrumb pathArray={pathArray} />
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Order Report</h5>
            </div>

            {/* Filters */}
            <Form className="mb-3">
                <Row>
                    <Col md={2}>
                        <Form.Label>Branch</Form.Label>
                        <Form.Select
                            value={filters.branchId}
                            onChange={(e) => updateFilters("branchId", e.target.value)}
                        >
                            <option value="">All Branches</option>
                            {branches.map((branch) => (
                                <option key={branch.branchId} value={branch.branchId}>
                                    {branch.branchName}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <Form.Label>Order From Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.fromDate}
                            onChange={(e) => updateFilters("fromDate", e.target.value)}
                        />
                    </Col>
                    <Col md={2}>
                        <Form.Label>Order To Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.toDate}
                            onChange={(e) => updateFilters("toDate", e.target.value)}
                        />
                    </Col>
                    <Col md={2}>
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                            value={filters.fieldType}
                            onChange={(e) => updateFilters("fieldType", e.target.value)}
                        >
                            <option value="">All</option>
                            <option value="Pending">Pending</option>
                            <option value="Confirmed">Confirmed</option>
                            <option value="Packed">Packed</option>
                            <option value="Shipped">Shipped</option>
                            <option value="Cancelled">Cancelled</option>
                            <option value="Delivered">Delivered</option>
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <Form.Label>{filters.fieldType ? `${filters.fieldType} From Date` : 'Delivery From Date'}</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.fieldFDate}
                            onChange={(e) => updateFilters("fieldFDate", e.target.value)}
                        />
                    </Col>
                    <Col md={2} >
                        <Form.Label>{filters.fieldType ? `${filters.fieldType} To Date` : 'Delivery To Date'}</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.fieldTDate}
                            onChange={(e) => updateFilters("fieldTDate", e.target.value)}
                        />
                    </Col>
                    <Col md={2} className="mt-2 d-flex align-items-end">
                        <Button variant="primary" onClick={() => fetchRecord()}>
                            Filter
                        </Button>
                        <Button variant="success" className="ms-2" onClick={exportData}>
                            Export
                        </Button>
                    </Col>
                </Row>
            </Form>

            {loaderVisible && <Loader />}

            {/* Table */}
            <Box>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowHeight={() => "auto"}
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    rowCount={totalRecords}
                    pageSizeOptions={[5, 10, 15, 20]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    onSortModelChange={setSortModel}
                    onFilterModelChange={setFilterModel}
                    loading={loading}
                    checkboxSelection
                    onRowSelectionModelChange={(row) => setRowSelectionModel(row)}
                    rowSelectionModel={rowSelectionModel}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            csvOptions: { disableToolbarButton: true },
                            printOptions: { disableToolbarButton: true },
                        },
                    }}
                    sx={{
                        [`& .${gridClasses.cell}`]: { py: 1 },
                    }}
                />
            </Box>
        </Container>
    );
};

export default SaleOrders;
